/**
 * useRevokeTrainingCompletion — Batch 18-E15
 *
 * Thin client wrapper around the verify-training-completion Edge
 * Function with `action: 'revoke'`. ALL authority and atomicity
 * guarantees live in the SECURITY DEFINER revoke_training_completion
 * RPC (see migration 20260604020000_training_verification_revocation_foundation.sql):
 *
 *   - SERVICE_WRITER exclusion
 *   - Technician self-revocation rejection
 *   - Revocation note REQUIRED (server-side enforced)
 *   - manager_verified_at IS NOT NULL prerequisite
 *   - Atomic INSERT 'revoked' audit row + UPDATE session pointer
 *     columns to NULL + restore completion_confidence from the prior
 *     'verified' audit row's snapshotted metadata (fallback 'likely')
 *
 * This hook performs ZERO direct DB writes. It cannot bypass the
 * Edge Function's guards or the RPC's transaction.
 *
 * No UI in 18-E15. The hook exists so a follow-on UI batch has a
 * stable surface to call.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RevokeTrainingCompletionInput {
  sessionId: string;
  note: string;
}

export interface RevokeTrainingCompletionResult {
  revocationId: string;
  sessionId: string;
  revoked_at: string;
  restored_confidence:
    | 'verified'
    | 'likely'
    | 'unverified'
    | 'suspicious';
  verified_by_role:
    | 'MANAGER'
    | 'OWNER'
    | 'ORG_ADMIN'
    | 'INTERNAL_ADMIN';
}

export function useRevokeTrainingCompletion() {
  const queryClient = useQueryClient();

  return useMutation<
    RevokeTrainingCompletionResult,
    Error,
    RevokeTrainingCompletionInput
  >({
    mutationFn: async ({ sessionId, note }) => {
      const { data, error } = await supabase.functions.invoke(
        'verify-training-completion',
        {
          body: {
            action: 'revoke',
            sessionId,
            note,
          },
        },
      );
      if (error) throw error;
      return data as RevokeTrainingCompletionResult;
    },
    onSuccess: (_result, variables) => {
      // Invalidate every query key that 18-E12 / 18-E13 use to surface
      // verification state so the UI re-fetches after revocation
      // clears the session pointer columns. Keys mirror the
      // useVerifyTrainingCompletion invalidation set plus the
      // Passport-side query introduced in 18-E13.
      queryClient.invalidateQueries({
        queryKey: ['consumption-sessions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['pending-manager-verifications'],
      });
      queryClient.invalidateQueries({
        queryKey: ['passport-verified-sessions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['training-completion-verifications', variables.sessionId],
      });
    },
  });
}
